export const PERSONALITY_PROFILES = {
  analytical_thinker: {
    title: "Analytical Thinker",

    shortSummary:
      "You approach problems with logic, structure, and careful reasoning.",

    longSummary:
      "Analytical Thinkers are naturally drawn to systems, patterns, and strategic problem-solving. They prefer environments where decisions are backed by evidence, structure, and clear thinking. They often excel in technical and intellectually demanding fields.",

    insights: [
      "You enjoy solving complex problems systematically.",
      "You prefer clarity and logical consistency.",
      "You tend to think before acting.",
      "You perform well in structured environments."
    ],

    strengths: [
      "Critical thinking",
      "Strategic reasoning",
      "Attention to detail",
      "Logical analysis"
    ],

    collaborationStyle:
      "Works best in focused teams with clearly defined responsibilities.",

    decisionMakingStyle:
      "Makes decisions carefully using logic, evidence, and analysis.",

    workStyle:
      "Prefers structured workflows and intellectually challenging tasks.",

    idealEnvironments: [
      "Structured organizations",
      "Technical environments",
      "Research-driven settings"
    ],

    cautions: [
      "May overthink decisions",
      "Can become too detail-focused",
      "May struggle with ambiguity"
    ],
    
    behavioralTraits: {
      structurePreference: "high",
      collaborationPreference: "low",
      ambiguityTolerance: "low",
      creativityOrientation: "moderate",
      emotionalOrientation: "low",
      adaptabilityPreference: "moderate",
      leadershipPreference: "moderate",
      technicalOrientation: "high"
    },

    behaviourProfile: {
      inclusion: 40,
      control: 90,
      affection: 35,
      thinking: 95,
      flexibility: 45
    }, 

    recommendedPrograms: [
      "Computer Science",
      "Engineering",
      "Economics",
      "Data Science"
    ]
  },

  creative_explorer: {
    title: "Creative Explorer",

    shortSummary:
      "You are imaginative, curious, and energized by new ideas and possibilities.",

    longSummary:
      "Creative Explorers thrive in environments that encourage innovation, self-expression, and experimentation. They enjoy discovering unconventional solutions and are often motivated by curiosity and creative freedom.",

    insights: [
      "You enjoy exploring new perspectives.",
      "You are naturally imaginative.",
      "You value creativity and flexibility.",
      "You dislike repetitive routines."
    ],

    strengths: [
      "Creativity",
      "Adaptability",
      "Innovation",
      "Expressive thinking"
    ],

    collaborationStyle:
      "Brings energy and fresh ideas into collaborative environments.",

    decisionMakingStyle:
      "Often guided by curiosity, intuition, and possibilities.",

    workStyle:
      "Prefers flexible and creative environments over rigid systems.",

    idealEnvironments: [
      "Creative industries",
      "Innovation-focused teams",
      "Flexible workspaces"
    ],

    cautions: [
      "May lose interest in repetitive tasks",
      "Can become distracted by too many ideas",
      "May resist rigid structure"
    ],

    behavioralTraits: {
      structurePreference: "low",
      collaborationPreference: "moderate",
      ambiguityTolerance: "high",
      creativityOrientation: "high",
      emotionalOrientation: "moderate",
      adaptabilityPreference: "high",
      leadershipPreference: "low",
      technicalOrientation: "moderate"
    },

    behaviourProfile: {
      inclusion: 65,
      control: 45,
      affection: 60,
      thinking: 75,
      flexibility: 95
    },

    recommendedPrograms: [
      "Design",
      "Media",
      "Marketing",
      "Architecture"
    ]
    },

  strategic_planner: {
    title: "Strategic Planner",

    shortSummary:
      "You are organized, goal-oriented, and naturally focused on planning and execution.",

    longSummary:
      "Strategic Planners thrive in structured environments where leadership, coordination, and long-term thinking are valued. They are comfortable managing responsibilities and making decisions that move projects toward clear goals.",

    insights: [
      "You naturally organize tasks and people.",
      "You think ahead before making decisions.",
      "You value structure and direction.",
      "You work well under responsibility."
    ],

    strengths: [
      "Leadership",
      "Organization",
      "Decision-making",
      "Strategic thinking"
    ],

    collaborationStyle:
      "Works effectively in leadership or coordination roles within teams.",

    decisionMakingStyle:
      "Makes decisions based on goals, efficiency, and long-term outcomes.",

    workStyle:
      "Prefers structured environments with clear expectations and measurable progress.",

    idealEnvironments: [
      "Corporate environments",
      "Leadership-driven teams",
      "Structured institutions"
    ],

    cautions: [
      "May become overly controlling",
      "Can struggle with unpredictability",
      "May prioritize outcomes over flexibility"
    ],

    behavioralTraits: {
      structurePreference: "high",
      collaborationPreference: "moderate",
      ambiguityTolerance: "low",
      creativityOrientation: "moderate",
      emotionalOrientation: "moderate",
      adaptabilityPreference: "moderate",
      leadershipPreference: "high",
      technicalOrientation: "moderate",
    },

    behaviourProfile: {
      inclusion: 50,
      control: 95,
      affection: 45,
      thinking: 85,
      flexibility: 25
    },

    recommendedPrograms: [
      "Business Administration",
      "Law",
      "Political Science",
      "Project Management"
    ]
  },

  versatile_collaborator: {
    title: "Versatile Collaborator",

    shortSummary:
      "You are adaptable, people-oriented, and effective in collaborative environments.",

    longSummary:
      "Versatile Collaborators thrive when working with others and adapting to changing situations. They balance communication, teamwork, and flexibility, making them effective in dynamic and relationship-focused environments.",

    insights: [
      "You work well with different personalities.",
      "You adapt easily to group dynamics.",
      "You value communication and teamwork.",
      "You balance independence with collaboration."
    ],

    strengths: [
      "Communication",
      "Adaptability",
      "Teamwork",
      "Emotional intelligence"
    ],

    collaborationStyle:
      "Excels in cooperative and people-focused environments.",

    decisionMakingStyle:
      "Balances logic with the needs and perspectives of others.",

    workStyle:
      "Prefers flexible and socially engaging environments.",

    idealEnvironments: [
      "Team-oriented workplaces",
      "People-focused industries",
      "Collaborative settings"
    ],

    cautions: [
      "May avoid conflict",
      "Can become overly accommodating",
      "May struggle with rigid systems"
    ],

    behavioralTraits: {
      structurePreference: "moderate",
      collaborationPreference: "high",
      ambiguityTolerance: "moderate",
      creativityOrientation: "moderate",
      emotionalOrientation: "high",
      adaptabilityPreference: "high",
      leadershipPreference: "moderate",
      technicalOrientation: "low"
    },

    behaviourProfile: {
      inclusion: 95,
      control: 65,
      affection: 95,
      thinking: 60,
      flexibility: 70
    },

    recommendedPrograms: [
      "Psychology",
      "Human Resources",
      "Education",
      "Public Relations"
    ]
  },

  practical_operator: {
    title: "Practical Operator",

    shortSummary:
      "You are dependable, action-oriented, and focused on practical execution.",

    longSummary:
      "Practical Operators prefer hands-on environments where results, consistency, and real-world application matter. They are grounded, reliable, and often excel in operational or implementation-focused roles.",

    insights: [
      "You prefer practical action over theory.",
      "You value reliability and consistency.",
      "You focus on getting tasks completed efficiently.",
      "You adapt best through direct experience."
    ],

    strengths: [
      "Execution",
      "Reliability",
      "Practical thinking",
      "Operational focus"
    ],

    collaborationStyle:
      "Works best in dependable teams with clearly defined responsibilities.",

    decisionMakingStyle:
      "Makes decisions based on practicality and immediate effectiveness.",

    workStyle:
      "Prefers structured, hands-on, and outcome-focused work.",

    idealEnvironments: [
      "Operational environments",
      "Hands-on industries",
      "Applied work settings"
    ],

    cautions: [
      "May resist abstract ideas",
      "Can become routine-focused",
      "May avoid experimentation"
    ],

    behavioralTraits: {
      structurePreference: "high",
      collaborationPreference: "moderate",
      ambiguityTolerance: "low",
      creativityOrientation: "low",
      emotionalOrientation: "moderate",
      adaptabilityPreference: "low",
      leadershipPreference: "low",
      technicalOrientation: "moderate"
    },

    behaviourProfile: {
      inclusion: 55,
      control: 55,
      affection: 55,
      thinking: 60,
      flexibility: 70
    },

    recommendedPrograms: [
      "Agriculture",
      "Construction",
      "Logistics",
      "Nursing"
    ]
  },

  independent_problem_solver: {
    title: "Independent Problem Solver",

    shortSummary:
      "You are self-directed, persistent, and motivated by solving difficult challenges independently.",

    longSummary:
      "Independent Problem Solvers thrive when given autonomy, ownership, and intellectually demanding challenges. They are resilient, investigative, and often excel in environments that reward initiative and deep focus.",

    insights: [
      "You prefer solving problems independently.",
      "You stay focused on difficult challenges.",
      "You value autonomy and ownership.",
      "You are motivated by competence and mastery."
    ],

    strengths: [
      "Independence",
      "Persistence",
      "Problem-solving",
      "Self-direction"
    ],

    collaborationStyle:
      "Prefers autonomy but contributes strongly when collaboration is purposeful.",

    decisionMakingStyle:
      "Makes independent and calculated decisions based on analysis and experience.",

    workStyle:
      "Prefers focused environments with minimal unnecessary interference.",

    idealEnvironments: [
      "Research environments",
      "Technical industries",
      "Independent project work"
    ],

    cautions: [
      "May isolate themselves too much",
      "Can become overly self-reliant",
      "May struggle with constant collaboration"
    ],

    behavioralTraits: {
      structurePreference: "moderate",
      collaborationPreference: "low",
      ambiguityTolerance: "high",
      creativityOrientation: "moderate",
      emotionalOrientation: "low",
      adaptabilityPreference: "high",
      leadershipPreference: "low",
      technicalOrientation: "high"
    },

    behaviourProfile: {
      inclusion: 25,
      control: 70,
      affection: 25,
      thinking: 85,
      flexibility: 65
    },

    recommendedPrograms: [
      "Cybersecurity",
      "Software Engineering",
      "Research",
      "Entrepreneurship"
    ]
  }
};